import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import useInfiniteScrollSentinel, { scrollTableToTop } from "hooks/useInfiniteScrollSentinel";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import ColourConstants from "helpers/colourConstants";
import PopupMenu from "components/Elements/PopupMenu";
import { makeStyles } from "tss-react/mui";
import TableStyle from "styles/application/TableStyle";
import { appPath, modelsPath } from "helpers/routePaths";
// Icon imports
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import { CircularProgress, Link } from "@mui/material";
import {
	isoDateWithoutTimeZone,
	createTableHeaderClickHandler,
} from "helpers/utils";
import { useOutletContext } from "react-router-dom";
import { ReactComponent as ExternalLinkSvg } from "assets/icons/external-link.svg";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import AutoFitContentInScreen from "components/Layouts/AutoFitContentInScreen";

// Init styled components
const AT = TableStyle();
// Size constant
const MAX_LOGO_HEIGHT = 47;

const media = "@media (max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	table: {
		borderStyle: "solid",
		fontFamily: "Roboto Condensed",
		fontSize: 14,
		overflowX: "auto",
		borderColor: ColourConstants.tableBorder,
		borderWidth: 1,
		borderRadius: 0,
	},
	tableBody: {
		whiteSpace: "noWrap",
	},
	tableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackground,
		fontWeight: "bold",
	},
	selectedTableHeadRow: {
		borderBottomColor: ColourConstants.tableBorder,
		borderBottomStyle: "solid",
		borderBottomWidth: 1,
		backgroundColor: ColourConstants.tableBackgroundSelected,
		fontWeight: "bold",
		color: "#FFFFFF",
	},
	nameRow: {
		width: 130,
		[media]: {
			width: 130,
		},
	},
	clientsRow: {
		width: "20%",
	},
	dataCell: {
		height: 85,
	},
	nameLink: {
		color: ColourConstants.activeLink,
	},
	clientLogo: {
		maxHeight: MAX_LOGO_HEIGHT,
	},
	noImage: {
		color: ColourConstants.commonText,
		opacity: "50%",
	},
	lastCell: {
		borderBottom: "none",
	},
	greater: {
		color: ColourConstants.red,
	},
}));

const ModelsListTable = forwardRef(({
	isSharableModel,
	data,
	columns,
	headers,
	handleSort,
	handleDeleteDialogOpen,
	handleDuplicateModalOpen,
	handleDuplicateAndTranslateModalOpen,
	handleViewVersionModalOpen,
	handleShareModelOpen,
	position,
	currentTableSort,
	reduceTableHeightBy,
	hasNextPage,
	isFetchingNextPage,
	fetchNextPage,
	isFetchNextPageError,
}, ref) => {
	// Init hooks
	const { classes, cx } = useStyles();
	const [convertedData, setConvertedData] = useState([]);
	const { access } = useOutletContext();

	const me =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const adminType = me?.adminType;

	const [anchorElMenu, setAnchorElMenu] = useState(null);
	const [selectedDataMenu, setSelectedDataMenu] = useState(null);

	const { sentinelRef } = useInfiniteScrollSentinel({
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		isError: isFetchNextPageError,
	});

	useImperativeHandle(ref, () => ({ gotoTop: () => scrollTableToTop() }));

	useEffect(() => {
		setConvertedData([]);

		const safeData = Array.isArray(data) ? data : [];
		let newData = safeData.map((item, index) => {
			let currentDate = new Date();
			let apiDate = isoDateWithoutTimeZone(
				item?.reviewDate ? item.reviewDate + "Z" : item.reviewDate
			);
			let isGreater =
				currentDate > new Date(item?.reviewDate ? item?.reviewDate + "Z" : "");
			return {
				...item,
				status: `${item?.status} ${
					item?.activeModelVersion
						? item?.active
							? " (Active)"
							: " (Inactive)"
						: ""
				}`,
				reviewDate: (
					<span className={`${isGreater ? classes.greater : ""}`}>
						{apiDate?.split(" ")[0]}
					</span>
				),
				departments: item?.departments?.join(", "),
				createdDate: item?.createdDate
					? isoDateWithoutTimeZone(item.createdDate + "Z")
					: "",
				modifiedDate: item?.modifiedDate
					? isoDateWithoutTimeZone(item.modifiedDate + "Z")
					: "",
			};
		});

		setConvertedData(newData);
	}, [data]);

	const handleSortClick = createTableHeaderClickHandler(
		currentTableSort,
		handleSort
	);

	const toolTipColumn = ["departments"];

	const handleTooltip = (col) => {
		const tooltipElement = document.querySelector(`#tooltip-${col} > div`);

		if (tooltipElement) {
			if (col === "departments") {
				const departmentsData = document.querySelector(
					`#taskExpandable${col.id} > #dataCellroles > div > p `
				);
				if (departmentsData?.textContent) {
					document.querySelector(`#tooltip-roles > div `).innerHTML =
						departmentsData;
				}
			}
		}
	};
	return (
		<>
			<AutoFitContentInScreen
				containsTable={true}
				reduceTableHeightBy={reduceTableHeightBy}
			>
				<Table aria-label="Table" className={classes.table}>
					<AT.TableHead>
						<TableRow>
							{Array.isArray(headers) && headers.map((header, i) => (
								<TableCell
									key={i}
									onClick={() => {
										handleSortClick(columns[i]);
									}}
									className={cx(classes.nameRow, {
										[classes.selectedTableHeadRow]:
											currentTableSort[0] === columns[i],
										[classes.tableHeadRow]: currentTableSort[0] !== columns[i],
									})}
								>
									<AT.CellContainer>
										{header}
										{currentTableSort[0] === columns[i] &&
										currentTableSort[1] === "desc" ? (
											<AT.DefaultArrow fill="#FFFFFF" />
										) : (
											<AT.DescArrow fill="#FFFFFF" />
										)}
									</AT.CellContainer>
								</TableCell>
							))}
						</TableRow>
					</AT.TableHead>
					<TableBody className={classes.tableBody}>
						{Array.isArray(convertedData) && convertedData.length ? (
							convertedData.map((row, index) => (
								<TableRow key={`${row.id}-${index}`}>
									{Array.isArray(columns) && columns.map((col, i, arr) => {
										return (
											<AT.DataCell
												key={col}
												style={
													toolTipColumn.includes(col)
														? {
																padding: "0 10px",
																width: "200px",
																height: "44px",
														  }
														: {}
												}
											>
												<AT.CellContainer key={col}>
													<AT.TableBodyText>
														{col === "fullName" ? (
															(() => {
																const isTranslating =
																	row.translationStatus === "InProgress";
																const isTranslationFailed =
																	row.translationStatus === "Failed";
																return (
																	<div
																		style={{
																			display: "flex",
																			flexDirection: "column",
																			alignItems: "flex-start",
																			gap: 4,
																		}}
																	>
																		<Link
																			underline="none"
																			onClick={(e) => {
																				if (isTranslating) {
																					e.preventDefault();
																					return;
																				}
																				if (e?.button !== 2) {
																					e.preventDefault();
																					window.open(
																						`${appPath}${modelsPath}/${row.devModelVersionID}`,
																						"_blank"
																					);
																				}
																			}}
																			href={
																				isTranslating
																					? undefined
																					: `${appPath}${modelsPath}/${row.devModelVersionID}`
																			}
																			style={{
																				color: isTranslating
																					? "#888"
																					: "rgb(17, 100, 206)",
																				cursor: isTranslating
																					? "not-allowed"
																					: "pointer",
																				display: "flex",
																				justifyContent: "flex-start",
																				alignItems: "center",
																				gap: 10,
																				pointerEvents: isTranslating
																					? "none"
																					: "auto",
																			}}
																		>
																			{row[col]}
																			{!isTranslating && (
																				<ExternalLinkSvg
																					style={{
																						width: "16px",
																						height: "16px",
																						marginBottom: 3,
																					}}
																				/>
																			)}
																		</Link>
																		{isTranslating && (
																			<span
																				style={{
																					display: "inline-flex",
																					alignItems: "center",
																					gap: 6,
																					fontSize: 12,
																					color: "#8a6d00",
																					backgroundColor: "#fff3cd",
																					border: "1px solid #ffe69c",
																					borderRadius: 12,
																					padding: "2px 8px",
																				}}
																				title={`Translating to ${
																					row.translationTargetLanguage ?? ""
																				}`}
																			>
																				<CircularProgress size={10} />
																				Translating
																				{row.translationTargetLanguage
																					? ` to ${row.translationTargetLanguage}${
																							row.translationCountry
																								? `-${row.translationCountry.toUpperCase()}`
																								: ""
																					  }`
																					: ""}
																				…
																			</span>
																		)}
																		{isTranslationFailed && (
																			<span
																				style={{
																					display: "inline-flex",
																					alignItems: "center",
																					gap: 6,
																					fontSize: 12,
																					color: "#a4262c",
																					backgroundColor: "#fde7e9",
																					border: "1px solid #f3b9bd",
																					borderRadius: 12,
																					padding: "2px 8px",
																					cursor: "help",
																				}}
																				title={
																					row.translationError ||
																					"Translation failed"
																				}
																			>
																				Translation failed
																			</span>
																		)}
																	</div>
																);
															})()
														) : toolTipColumn.includes(col) ? (
															<HtmlTooltip
																id={`tooltip-${col}`}
																title={row[col]}
																onOpen={() => handleTooltip(col)}
															>
																<p
																	className="max-two-line"
																	draggable="true"
																	style={{
																		padding: "0 10px",
																		width: "200px",
																		height: "44px",
																		textOverflow: "ellipsis",
																		whiteSpace: "wrap",
																	}}
																>
																	{row[col]}
																</p>
															</HtmlTooltip>
														) : (
															row[col]
														)}
													</AT.TableBodyText>

													{arr.length === i + 1 ? (
														<AT.DotMenu
															onClick={(e) => {
																setAnchorElMenu(
																	anchorElMenu === e.currentTarget
																		? null
																		: e.currentTarget
																);
																setSelectedDataMenu(
																	anchorElMenu === e.currentTarget ? null : index
																);
															}}
														>
															<AT.TableMenuButton>
																<MenuIcon />
															</AT.TableMenuButton>

															<PopupMenu
																index={index}
																selectedData={selectedDataMenu}
																anchorEl={anchorElMenu}
																isLast={index === data.length - 1}
																id={row.id}
																clickAwayHandler={() => {
																	setAnchorElMenu(null);
																	setSelectedDataMenu(null);
																}}
																menuData={[
																	{
																		name:
																			access === "R" ||
																			row.activeModelVersionID ===
																				row.devModelVersionID
																				? "View"
																				: "Edit",
																		handler: () => {
																			window.open(
																				`${appPath}${modelsPath}/${row.devModelVersionID}`
																			);
																		},
																		isDelete: false,
																		ShouldHide: false,
																	},
																	{
																		name: "View Version",
																		handler: handleViewVersionModalOpen,
																		isDelete: false,
																		ShouldHide: false,
																	},
																	{
																		name: "Duplicate",
																		handler: () => {
																			handleDuplicateModalOpen(row);
																		},
																		isDelete: false,
																		ShouldHide: false,
																	},
																	{
																		name: "Duplicate and Translate",
																		handler: () => {
																			handleDuplicateAndTranslateModalOpen(row);
																		},
																		isDelete: false,
																		ShouldHide: false,
																	},
																	{
																		name: "Share",
																		handler: () => {
																			handleShareModelOpen(row);
																		},
																		isDelete: false,
																		ShouldHide: !(
																			isSharableModel &&
																			(row.isSharable || adminType === 1) &&
																			row.isPublish
																		),
																	},
																	{
																		name: "Remove",
																		handler: handleDeleteDialogOpen,
																		isDelete: true,
																		ShouldHide: !position?.allowPublish,
																	},
																].filter((x) => {
																	if (access === "F" && !x.ShouldHide)
																		return true;
																	if (access === "E" && !x.ShouldHide) {
																		if (
																			x.name === "Edit" ||
																			x.name === "View" ||
																			x.name === "View Version"
																		) {
																			return true;
																		} else {
																			return false;
																		}
																	}
																	if (access === "R" && !x.ShouldHide) {
																		if (
																			x.name === "Edit" ||
																			x.name === "View" ||
																			x.name === "View Version"
																		)
																			return true;
																		else return false;
																	}
																	return false;
																})}
															/>
														</AT.DotMenu>
													) : null}
												</AT.CellContainer>
											</AT.DataCell>
										);
									})}
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell>No any records found</TableCell>
							</TableRow>
						)}
						{hasNextPage && (
							<TableRow ref={sentinelRef}>
								<TableCell
									colSpan={columns?.length}
									style={{ padding: "12px 10px", textAlign: "center", border: 0 }}
								>
									{isFetchingNextPage ? <b>Loading...</b> : " "}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</AutoFitContentInScreen>
		</>
	);
});

export default ModelsListTable;
